import React, { ReactElement, useContext, useEffect, useState } from 'react';
import useSnackbar from '../../../../hooks/useSnackbar';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../../contexts/auth';
import { useAuthRedirect } from '../../../../hooks/useAuthRedirect';
import { usePageLayout } from '../../../../hooks/usePageLayout';
import { isOperatorStaff } from '../../../../utils/permission';
import { useProxyAccessControl } from '../../../../hooks/useProxyAccessControl';
import {
  Avatar,
  Box,
  Grid2,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  gray10,
  gray100,
  gray20,
  gray40,
  gray80,
  navy50,
  white,
} from '../../../../constants/colors';
import { boldXxl, regularSm, spHeaderTitle } from '../../../../constants/location';
import MetaTitle from '../../../utils/MetaTitle';
import SideBar from '../../../uiParts/menu/SideBar';
import { DocumentSnapshot } from 'firebase/firestore';
import InfiniteScroll from 'react-infinite-scroll-component';
import CustomLoading from '../../../uiParts/form/CustomLoading';
import DescriptionIcon from '@mui/icons-material/Description';
import CustomSnackbar from '../../../uiParts/form/CustomSnackbar';
import dayjs from 'dayjs';
import { PatternType } from '../../../../types/pattern';
import { getPatternsPaginated } from '../../../../api/firebase/firestore/pattern/read';
import { geminiModel, patternStatus } from '../../../../constants/enum';
import { getUser } from '../../../../api/firebase/firestore/user/read';
import { getCompany } from '../../../../api/firebase/firestore/company/read';
import NoAccountIcon from '../../../assets/Account circle.svg';

interface PatternWithUserAndCompanyType extends PatternType {
  showName?: string;
  iconUrl?: string;
  companyName?: string;
}

const AdminAllPatterns = (): ReactElement => {
  const [loading, setLoading] = useState<boolean>(false);
  const { errorText, infoText, snackbarOpen, setErrorText, setSnackbarOpen } = useSnackbar();
  const navigate = useNavigate();
  // 未認証ならログイン画面へ退避する（元URLは ?redirect= で引き継ぐ。#3203）
  useAuthRedirect();
  const { user, isLoading } = useContext(AuthContext);
  const { sideBarCollapsed, toggleSidebar, styles: layoutStyles } = usePageLayout();

  // 代理モード中はアクセス禁止
  useProxyAccessControl();

  // データ取得関連
  const resultLimit = 50;
  const [showData, setShowData] = useState<PatternWithUserAndCompanyType[]>([]);
  const [lastVisible, setLastVisible] = useState<DocumentSnapshot | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [showDataFlg, setShowDataFlg] = useState<boolean | null>(null);

  // ユーザー・会社のキャッシュ（累積して保持）
  const [userMap, setUserMap] = useState<Map<string, { name?: string; icon_url?: string }>>(
    new Map(),
  );
  const [companyMap, setCompanyMap] = useState<Map<string, string>>(new Map());

  const styles = {
    avatar: {
      height: '24px',
      width: '24px',
    },
    header: {
      alignItems: 'center',
      background: white,
      boxSizing: 'border-box',
      display: 'flex',
      height: '80px',
      justifyContent: 'space-between',
      padding: '0 24px',
      width: '100%',
      '@media screen and (max-width:960px)': {
        height: '48px',
        padding: '0 16px',
      },
    },
    headerTitle: {
      ...boldXxl,
      color: gray100,
      '@media screen and (max-width:960px)': {
        ...spHeaderTitle,
      },
    },
    icon24: {
      height: '24px',
      marginLeft: '0px',
      width: '24px',
    },
    table: {
      minWidth: '1200px',
      width: '100%',
    },
    tableCellAvatar: {
      alignItems: 'center',
      display: 'flex',
      gap: '8px',
      height: '48px',
      padding: '0 16px',
      whiteSpace: 'nowrap',
    },
    tableDataText: {
      ...regularSm,
      color: gray100,
      padding: '0 16px',
      whiteSpace: 'nowrap',
    },
    tableHeadText: {
      ...regularSm,
      color: gray80,
      padding: '0 16px',
      whiteSpace: 'nowrap',
    },
    tableHeadRow: {
      height: '48px',
    },
    tableDataRow: {
      height: '48px',
      '&:hover': {
        backgroundColor: gray20,
        cursor: 'pointer',
      },
    },
    tableSection: {
      backgroundColor: white,
      border: '1px solid' + gray40,
      borderRadius: '4px',
      boxSizing: 'border-box',
      margin: '0 32px',
      minHeight: '560px',
      padding: '16px 24px 56px',
      width: 'calc(100% - 64px)',
      '@media screen and (max-width:960px)': {
        border: 'none',
        borderRadius: 0,
        margin: 0,
        width: '100%',
      },
    },
    tableWrapper: {
      overflowX: 'auto',
      width: '100%',
      WebkitOverflowScrolling: 'touch',
    },
    PatternNameText: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      maxWidth: '200px',
      display: 'inline-block',
      verticalAlign: 'middle',
    },
    CompanyNameText: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      maxWidth: '150px',
      display: 'inline-block',
      verticalAlign: 'middle',
    },
    ItemCountText: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      minWidth: '80px',
      display: 'inline-block',
      verticalAlign: 'middle',
    },
    showUserNameText: {
      display: 'inline-block',
      verticalAlign: 'middle',
      wordBreak: 'break-word' as const,
    },
    icon: {
      width: '56px',
      height: '56px',
      margin: '80px 0 24px 0',
      '@media screen and (max-width: 600px)': {
        margin: '24px 0 24px 0',
      },
    },
    title: {
      whiteSpace: 'pre-wrap',
      mb: 2,
      fontWeight: '500',
      color: gray100,
      fontSize: '22px',
      fontStyle: 'normal',
      lineHeight: '20px',
      '@media screen and (max-width: 600px)': {
        marginBottom: '12px',
      },
    },
    titleAndStatusWrapper: {
      flexGrow: 1,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginRight: '18px',
      '@media screen and (max-width: 960px)': {
        marginRight: '0',
        fontSize: '12px',
      },
    },
  };

  useEffect(() => {
    if (isLoading) return;
    if (user === null) {
      return;
    }
    // 運営スタッフ（role=10,11）以外はアクセス不可
    if (!isOperatorStaff(user.role)) {
      navigate('/home');
    }
  }, [user, isLoading, navigate]);

  useEffect(() => {
    // 認証ロード中は何もしない
    if (isLoading) return;

    // ユーザーが存在しない場合はログアウト処理に任せる
    if (!user) return;

    // 運営スタッフ（role=10,11）以外はデータ取得しない
    if (!isOperatorStaff(user.role)) return;

    const fetchData = async (): Promise<void> => {
      await fetchPatterns(null);
    };
    fetchData();
  }, [user, isLoading]);

  // スクロールで追加データ取得
  const fetchMoreData = (): void => {
    if (!loading && hasMore) {
      fetchPatterns(lastVisible);
    }
  };

  const fetchPatterns = async (currentLastVisible: DocumentSnapshot | null): Promise<void> => {
    if (loading || !user) return;

    // 運営スタッフ（role=10,11）以外は処理を中断
    if (!isOperatorStaff(user.role)) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // 読取パターン一覧を取得
      const {
        results: patterns,
        lastVisible: newPatternLastVisible,
        isLastPage,
      } = await getPatternsPaginated(resultLimit, currentLastVisible, undefined, true);

      // 読取パターンに含まれるユニークなユーザーIDと企業IDを抽出
      const uniqueUserIds = Array.from(new Set(patterns.map(t => t.created_by).filter(Boolean)));
      const uniqueCompanyIds = Array.from(new Set(patterns.map(t => t.company_id).filter(Boolean)));

      // キャッシュにないユーザーIDと企業IDのみ取得
      const newUserMap = new Map(userMap);
      const newCompanyMap = new Map(companyMap);

      // まだキャッシュにないユーザーを取得
      const userIdsToFetch = uniqueUserIds.filter(
        (uid): uid is string => typeof uid === 'string' && !newUserMap.has(uid),
      );

      if (userIdsToFetch.length > 0) {
        const userPromises = userIdsToFetch.map(uid => getUser(uid));
        const users = await Promise.all(userPromises);
        users.forEach((userData, index) => {
          if (userData) {
            newUserMap.set(userIdsToFetch[index], {
              name: userData.name,
              icon_url: userData.icon_url,
            });
          }
        });
        setUserMap(newUserMap);
      }

      // まだキャッシュにない企業を取得
      const companyIdsToFetch = uniqueCompanyIds.filter(
        (cid): cid is string => typeof cid === 'string' && !newCompanyMap.has(cid),
      );

      if (companyIdsToFetch.length > 0) {
        const companyPromises = companyIdsToFetch.map(cid => getCompany(cid));
        const companies = await Promise.all(companyPromises);
        companies.forEach((companyData, index) => {
          if (companyData) {
            newCompanyMap.set(companyIdsToFetch[index], companyData.company_name || '不明');
          }
        });
        setCompanyMap(newCompanyMap);
      }

      // 取得した情報をマッピング
      const updatedResults = patterns.map(data => {
        const userData = newUserMap.get(data.created_by);
        const companyName = data.company_id ? newCompanyMap.get(data.company_id) || '不明' : '不明';

        return {
          ...data,
          showName: userData?.name,
          iconUrl: userData?.icon_url,
          companyName,
        } as PatternWithUserAndCompanyType;
      });

      // stateを更新
      setShowData(prevData =>
        currentLastVisible === null ? updatedResults : [...prevData, ...updatedResults],
      );
      setLastVisible(newPatternLastVisible);
      setHasMore(!isLastPage);

      // 初回読み込み時のみshowDataFlgを設定
      if (currentLastVisible === null) {
        setShowDataFlg(updatedResults.length > 0);
      }
    } catch (e) {
      // 初回読み込み時のエラーの場合、0件として扱う
      if (currentLastVisible === null) {
        setShowDataFlg(false);
        setShowData([]);
      } else {
        setErrorText('データ取得に失敗しました');
        setSnackbarOpen(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={layoutStyles.pageWrapper}>
      <MetaTitle />
      <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
        <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
      </Box>

      <Box
        sx={[
          layoutStyles.pageContent,
          sideBarCollapsed && layoutStyles.pageContentCollapsed,
          {
            '@media screen and (max-width:960px)': {
              paddingBottom: '84px',
            },
          },
        ]}
      >
        <Box sx={layoutStyles.header}>
          <Box sx={styles.titleAndStatusWrapper}>
            <Typography sx={styles.headerTitle}>全ての読取パターン</Typography>
          </Box>
        </Box>
        <Grid2 container flexDirection={'column'} gap={'16px'} width={'100%'}>
          <Box sx={styles.tableSection}>
            {showDataFlg === null ? (
              <CustomLoading loading={true} />
            ) : showDataFlg ? (
              <Box sx={styles.tableWrapper}>
                <InfiniteScroll
                  dataLength={showData.length}
                  next={fetchMoreData}
                  hasMore={hasMore}
                  loader={<CustomLoading loading={loading} />}
                >
                  <Table sx={styles.table}>
                    <TableHead>
                      <TableRow sx={styles.tableHeadRow}>
                        <TableCell sx={styles.tableHeadText}>読取パターン名</TableCell>
                        <TableCell sx={styles.tableHeadText}>会社名</TableCell>
                        <TableCell sx={styles.tableHeadText}>モデル</TableCell>
                        <TableCell sx={styles.tableHeadText}>読取項目数</TableCell>
                        <TableCell sx={styles.tableHeadText}>更新日</TableCell>
                        <TableCell sx={styles.tableHeadText}>作成者</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {showData.map((data, index) => {
                        const isDeleted = data.status === patternStatus.deleted;
                        return (
                          <TableRow
                            key={index}
                            onClick={(): void => navigate(`/admin/company/${data.company_id}`)}
                            sx={{
                              ...styles.tableDataRow,
                              ...(isDeleted && { opacity: 0.6, backgroundColor: gray10 }),
                            }}
                          >
                            {/* 読取パターン名 */}
                            <TableCell sx={styles.tableDataText}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Tooltip title={data.pattern_name}>
                                  <span style={styles.PatternNameText}>{data.pattern_name}</span>
                                </Tooltip>
                                {isDeleted && (
                                  <Box
                                    component="span"
                                    sx={{
                                      display: 'inline-block',
                                      px: 1,
                                      py: 0.25,
                                      borderRadius: '4px',
                                      backgroundColor: '#e0e0e0',
                                      color: '#757575',
                                      fontSize: '0.7rem',
                                      fontWeight: 'bold',
                                      whiteSpace: 'nowrap',
                                      flexShrink: 0,
                                    }}
                                  >
                                    削除済み
                                  </Box>
                                )}
                              </Box>
                            </TableCell>

                            {/* 会社名 */}
                            <TableCell sx={styles.tableDataText}>
                              <Tooltip title={data.companyName || '不明'}>
                                <span style={styles.CompanyNameText}>
                                  {data.companyName || '不明'}
                                </span>
                              </Tooltip>
                            </TableCell>

                            {/* モデル */}
                            <TableCell sx={styles.tableDataText}>
                              {data.gemini_model === geminiModel.pro_2_5 ? (
                                <Box
                                  component="span"
                                  sx={{
                                    display: 'inline-block',
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: '4px',
                                    backgroundColor: navy50,
                                    color: white,
                                    fontSize: '0.7rem',
                                    fontWeight: 'bold',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  Pro
                                </Box>
                              ) : (
                                <Box
                                  component="span"
                                  sx={{
                                    display: 'inline-block',
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: '4px',
                                    backgroundColor: gray40,
                                    color: gray80,
                                    fontSize: '0.7rem',
                                    fontWeight: 'bold',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  Flash
                                </Box>
                              )}
                            </TableCell>

                            {/* 読取項目数 */}
                            <TableCell sx={styles.tableDataText}>
                              <Tooltip title={data.item_count.toString()}>
                                <span style={styles.ItemCountText}>{data.item_count}</span>
                              </Tooltip>
                            </TableCell>

                            {/* 更新日 */}
                            <TableCell sx={styles.tableDataText}>
                              {dayjs(data.updated_at.seconds * 1000).format('YYYY/MM/DD')}
                            </TableCell>

                            {/* 作成者 */}
                            <TableCell sx={styles.tableCellAvatar}>
                              <Avatar src={data.iconUrl || NoAccountIcon} sx={styles.avatar} />
                              <Typography>
                                <Tooltip title={data.showName}>
                                  <span style={styles.showUserNameText}>{data.showName}</span>
                                </Tooltip>
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </InfiniteScroll>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <DescriptionIcon sx={styles.icon} />
                <Typography sx={styles.title}>読取パターンがありません</Typography>
              </Box>
            )}
          </Box>
        </Grid2>
      </Box>

      <CustomSnackbar
        open={snackbarOpen}
        onClose={(): void => setSnackbarOpen(false)}
        text={errorText}
        infoText={infoText}
      />

      <CustomLoading loading={loading} />
    </Box>
  );
};

export default AdminAllPatterns;
